import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import { ROOT_API } from "../../api/config";

const DEBOUNCE_MS = 350;
const MIN_CHARS = 3;

/**
 * Nominatim-backed address autocomplete.
 *
 * Debounced (350ms) search against `apis/address-search?q=...`. On selecting a
 * candidate, lifts the structured address up via `onChange`:
 *   { road, house_number, settlement, latitude, longitude, osm_id }
 *
 * House number is captured by a separate input (OSM frequently lacks it); it is
 * prefilled from the candidate's `house_number` when present.
 */
const AddressPicker = ({ value, onChange, initialQuery }) => {
  const intl = useIntl();

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [houseNumber, setHouseNumber] = useState(value?.house_number || "");

  const debounceRef = useRef(null);
  const cancelRef = useRef(null);
  const seededRef = useRef(false);

  // Pre-seed the search box with the town from an SEO landing page (/grad/<slug> CTA), so a
  // visitor arrives with their town already filled and only picks the exact street. One-shot.
  useEffect(() => {
    if (!seededRef.current && initialQuery) {
      seededRef.current = true;
      setInputValue(initialQuery);
    }
  }, [initialQuery]);

  const emit = (candidate, house) => {
    if (!candidate) {
      onChange?.(null);
      return;
    }
    onChange?.({
      road: candidate.road || "",
      house_number: house || "",
      settlement: candidate.settlement || "",
      place: candidate.place || candidate.settlement || "",
      latitude: candidate.lat,
      longitude: candidate.lon,
      osm_id: candidate.osm_id,
    });
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const query = inputValue.trim();
    if (query.length < MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(async () => {
      if (cancelRef.current) {
        cancelRef.current.cancel?.();
      }
      const source = axios.CancelToken.source();
      cancelRef.current = source;

      setLoading(true);
      try {
        const axiosObject = axios.create({
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        const res = await axiosObject.get(
          `${ROOT_API}apis/address-search?q=${encodeURIComponent(query)}`,
          { cancelToken: source.token }
        );
        setOptions(res?.data?.data || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          setOptions([]);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue]);

  const handleSelect = (event, candidate) => {
    setSelected(candidate);
    const prefilled = candidate?.house_number || "";
    setHouseNumber(prefilled);
    emit(candidate, prefilled);
  };

  const handleHouseNumberChange = (event) => {
    const house = event.target.value;
    setHouseNumber(house);
    emit(selected, house);
  };

  return (
    <>
      <FormControl fullWidth sx={{ marginTop: "12px" }}>
        <Autocomplete
          id="address-search"
          options={options}
          filterOptions={(opts) => opts}
          getOptionLabel={(option) =>
            option?.label || option?.display_name || ""
          }
          renderOption={(props, option) => (
            <li {...props} key={option.osm_id}>
              <span style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 600 }}>
                  {option.road || option.label}
                </span>
                {option.place || option.settlement || option.county ? (
                  <span style={{ fontSize: "0.82rem", color: "#5b6577" }}>
                    {option.place || option.settlement || option.county}
                  </span>
                ) : null}
              </span>
            </li>
          )}
          isOptionEqualToValue={(option, val) => option?.osm_id === val?.osm_id}
          value={selected}
          onChange={handleSelect}
          inputValue={inputValue}
          onInputChange={(event, newValue) => setInputValue(newValue)}
          loading={loading}
          loadingText={intl.formatMessage({ id: "searching" })}
          noOptionsText={
            inputValue.trim().length < MIN_CHARS ? (
              intl.formatMessage({ id: "address_search_hint" })
            ) : (
              // Failed address match = a real signup drop-off — soften it with the confused beaver
              // + a "let's try again" nudge instead of a cold "no results".
              <div style={{ textAlign: "center", padding: "10px 4px" }}>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-confused-trim.png`}
                  alt=""
                  aria-hidden="true"
                  style={{ height: 68, width: "auto", marginBottom: 8 }}
                />
                <div style={{ fontSize: "0.9rem", color: "#5b6577", lineHeight: 1.45 }}>
                  {intl.formatMessage({ id: "no_addresses_found" })}
                </div>
              </div>
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label={intl.formatMessage({ id: "address" })}
              placeholder={intl.formatMessage({ id: "address_search_hint" })}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <FormControl fullWidth sx={{ marginTop: "12px" }}>
        <TextField
          id="house-number"
          variant="standard"
          label={intl.formatMessage({ id: "house_number" })}
          value={houseNumber}
          onChange={handleHouseNumberChange}
        />
      </FormControl>
    </>
  );
};

export default AddressPicker;
